import { DocumentTextIcon } from '@heroicons/vue/24/outline'

/**
 * Jediný zdroj pravdy pro nabízené služby. Konzumuje ho PricingSection
 * (výpis služeb), AddonServicesSection i výběr služby v kontaktním formuláři,
 * takže se nabídka mezi sekcemi nikdy nerozejde.
 *
 * Místo více tarifů na publikum (start/klasik/professional) máme JEDEN komplexní
 * balíček per publikum — klient zpracovává všechny daňové i mzdové agendy,
 * cena je vstupní hodnota a od ní se diferencuje individuálně.
 *
 * @typedef {{ id: string, audience: string, heading: string, price: string, description: string, features: string[] }} Plan
 */

export const plans = [
    {
        id: 'osvc',
        audience: 'OSVČ',
        heading: 'Fyzické osoby (OSVČ)',
        price: 'Od 500 Kč / měsíc',
        description: 'Komplexní servis pro živnostníky — od evidence po zastupování na úřadech.',
        features: [
            'Kompletní vedení daňové evidence',
            'Zpracování daňového přiznání k dani z příjmů fyzických osob',
            'Přehledy pro OSSZ a zdravotní pojišťovny',
            'Zpracování mezd včetně komunikace s OSSZ a zdravotními pojišťovnami',
            'Zpracování přiznání k DPH',
            'Kontrolní hlášení (KH)',
            'Souhrnné hlášení (SH)',
            'Zastupování před finančním úřadem na základě plné moci',
            'Poradenství a konzultace',
        ],
    },
    {
        id: 'firmy',
        audience: 'Firma',
        heading: 'Společnosti (s.r.o.)',
        price: 'Od 800 Kč / měsíc',
        description: 'Komplexní účetnictví pro s.r.o., startupy a malé i střední firmy.',
        features: [
            'Kompletní vedení účetnictví',
            'Účetní závěrka a zpracování daňového přiznání',
            'Zpracování mezd včetně komunikace s OSSZ a zdravotními pojišťovnami',
            'Zpracování přiznání k DPH',
            'Kontrolní hlášení (KH)',
            'Souhrnné hlášení (SH)',
            'Zastupování před finančním úřadem na základě plné moci',
            'Poradenství a konzultace',
        ],
    },
]

/**
 * Sdílená vstupní cena pro doplňkové (jednorázové) služby. Klient drží jednu
 * sazbu pro celou kategorii — konkrétní cena se domlouvá podle rozsahu.
 */
export const addonsStartingPrice = 'Od 1\u00A0500\u00A0Kč'

/**
 * Jednorázové (doplňkové) služby bez celoroční spolupráce. Přiznání
 * i přehledy tvoří JEDNU společnou službu se společnou vstupní cenou.
 *
 * @typedef {{ id: string, name: string, description: string, icon: object, features: string[] }} Addon
 */
export const addonServices = [
    {
        id: 'addon-jednorazove',
        name: 'Daňové přiznání a přehledy',
        description:
            'Zpracujeme přiznání i přehledy jednorázově, bez nutnosti celoroční spolupráce — pro zaměstnance, OSVČ i pronajímatele.',
        icon: DocumentTextIcon,
        features: [
            'Zpracování daňového přiznání k dani z příjmů fyzických osob',
            'Přehledy pro OSSZ a zdravotní pojišťovny',
        ],
    },
]

/**
 * Seskupené možnosti pro výběr služby v kontaktním formuláři.
 *
 * @returns {Array<{ label: string, options: Array<{ id: string, name: string, price?: string }> }>}
 */
export function serviceSelectGroups() {
    return [
        {
            label: 'Účetní servis',
            options: plans.map((plan) => ({ id: plan.id, name: plan.heading, price: plan.price })),
        },
        {
            label: 'Doplňkové služby',
            options: addonServices.map((addon) => ({
                id: addon.id,
                name: addon.name,
                price: addonsStartingPrice,
            })),
        },
    ]
}

/**
 * Převede id služby na Selection objekt pro usePlanSelection.
 *
 * @param {string} id
 * @returns {import('@/composables/usePlanSelection').Selection | null}
 */
export function findServiceById(id) {
    const plan = plans.find((p) => p.id === id)

    if (plan) {
        return { id: plan.id, type: 'plan', name: plan.heading, price: plan.price }
    }

    const addon = addonServices.find((a) => a.id === id)

    if (addon) {
        return { id: addon.id, type: 'addon', name: addon.name, price: addonsStartingPrice }
    }

    return null
}
